import { DataHandlerMedia, type Media } from "luxedo-data"
import { ACTIVE_JOB_STATES, LuxedoRPC, type JobStatus } from "luxedo-rpc"

type ProcessingData = {
	progress: number
	status: "success" | "fail" | "processing" | "queued"
	/**
	 * For a failure: the media's completion marker (mediaCompletionMarker) at the moment it was
	 * recorded. The tile shows Failed only while the media still carries that marker, so a later
	 * successful run, which moves it, clears the badge; `!media.size` could not do that, because a
	 * re-uploaded media already has a size (PR #1052 review).
	 */
	failedMarker?: string | null
}

/** The media field a finished processing job moves: updated_at (touched by process_on_complete). */
export function mediaCompletionMarker(media: Media | undefined): string | null {
	if (!media) return null
	return mediaCompletionMarkerOf(media.updated_at)
}

/** Same marker from the field alone, for a component reading it through Reactivity.propOf. */
export function mediaCompletionMarkerOf(updatedAt: Date | undefined): string {
	return String(updatedAt?.getTime() ?? "none")
}

/** How often the portal asks the API about jobs it cannot hear live (after a refresh, or in another tab). */
export const MEDIA_STATUS_POLL_MS = 10_000

class MediaProgressHandler {
	progress: { [index: number]: ProcessingData } = $state({})

	/** Media ids whose job this page is following through the status poll */
	private watched = new Set<number>()

	/**
	 * The media's updated_at as this page held it when the job was first seen in flight. A
	 * finished job moves it (process_on_complete touches the row); a job whose registry record
	 * vanished without an outcome has not. `size` cannot serve: a re-upload already has one.
	 */
	private markerWhenFollowed: { [mediaId: number]: string | null } = {}
	private pollTimer: ReturnType<typeof setInterval> | null = null

	constructor() {
		this.listenToProgress()
	}

	private rememberMarker(mediaId: number) {
		if (mediaId in this.markerWhenFollowed) return
		this.markerWhenFollowed[mediaId] = this.completionMarker(mediaId)
	}

	private completionMarker(mediaId: number): string | null {
		return mediaCompletionMarker(DataHandlerMedia.get(mediaId))
	}

	private handleUploadProgress = (mediaId: number, progress: number) => {
		this.rememberMarker(mediaId)
		this.progress[mediaId] = {
			progress: progress,
			status: "processing",
		}
	}

	/** The GPU worker found the account at its concurrency cap; the job will start when a slot frees. */
	private handleUploadQueued = (mediaId: number) => {
		this.rememberMarker(mediaId)
		this.progress[mediaId] = {
			progress: 0,
			status: "queued",
		}
	}

	/**
	 * The upload finished but processing could not be dispatched (the RPC rejected). Recorded as a
	 * failure so the tile shows Failed + Retry instead of a thumbnail-less row; a toast alone is
	 * gone in seconds and never reaches assistive tech (review council, 2026-09-24).
	 */
	public markDispatchFailed(mediaId: number) {
		this.handleUploadFail(mediaId)
	}

	private handleUploadFail = (mediaId: number) => {
		const existingProgress = this.progress[mediaId]?.progress ?? 0
		this.progress[mediaId] = {
			progress: existingProgress,
			status: "fail",
			failedMarker: this.completionMarker(mediaId),
		}
		delete this.markerWhenFollowed[mediaId]
		this.watched.delete(mediaId)
	}

	private handleUploadSuccess = (mediaId: number) => {
		DataHandlerMedia.pull([mediaId])
		this.progress[mediaId] = {
			status: "success",
			progress: 1,
		}
		delete this.markerWhenFollowed[mediaId]
		this.watched.delete(mediaId)
	}

	public setTemporaryProgress(mediaId: number, progress: number) {
		this.rememberMarker(mediaId)
		this.progress[mediaId] = {
			progress,
			status: "processing",
		}
		this.watch([mediaId])
	}

	/**
	 * Follow these media through the API's job registry until each one finishes. Live gpq events
	 * only reach the session that started a job, so after a refresh (or in a second tab) this poll
	 * is the only way the page learns that a file is queued, how far along it is, that it finished,
	 * or that it failed.
	 */
	public watch(mediaOrIds: Array<Media | number>) {
		for (const item of mediaOrIds) {
			const id = typeof item === "number" ? item : item.id
			this.watched.add(id)
		}
		if (this.watched.size) {
			this.pollSoon()
			this.ensurePolling()
		}
	}

	private pollSoonTimer: ReturnType<typeof setTimeout> | null = null

	/** Many callers watch in the same moment (a listing syncing after each file of an upload batch
	 * lands); answer them with one request rather than one each. Same shape as ProgressContext. */
	private pollSoon() {
		if (this.pollSoonTimer) return
		this.pollSoonTimer = setTimeout(() => {
			this.pollSoonTimer = null
			void this.poll()
		}, 250)
	}

	/** Ask about every media in a listing, and keep asking while any of them is in flight. */
	public async syncStatuses(media: Array<Media>) {
		// Every media, not only those without a size: a re-upload into an existing media has a size
		// from its first run, and its job in flight was invisible after a reload (PR #1052 review).
		// One batched request per listing; an id with no registry record is dropped on the first
		// answer, so idle media cost nothing after it.
		if (!media.length) return
		this.watch(media)
	}

	private ensurePolling() {
		if (this.pollTimer) return
		this.pollTimer = setInterval(() => void this.poll(), MEDIA_STATUS_POLL_MS)
	}

	private stopPollingIfIdle() {
		if (this.watched.size || !this.pollTimer) return
		clearInterval(this.pollTimer)
		this.pollTimer = null
	}

	private async poll() {
		const ids = [...this.watched]
		if (!ids.length) return this.stopPollingIfIdle()
		let status: { [mediaId: string]: JobStatus }
		try {
			status = (await LuxedoRPC.api.media.media_processing_status(ids)).status
		} catch (err) {
			console.error("Could not fetch media processing status", err)
			return
		}

		for (const id of ids) {
			const entry = status[String(id)]
			if (!entry) {
				// Nothing on record: it never started (idle), its record expired after finishing, or
				// its worker died and never wrote an outcome. For a media we saw in flight, absence
				// must never mean success on its own: the media row decides (onLost).
				if (this.progress[id]?.status === "processing" || this.progress[id]?.status === "queued") {
					void this.onLost(id)
				}
				this.watched.delete(id)
				continue
			}
			this.applyStatus(id, entry)
		}
		this.stopPollingIfIdle()
	}

	/**
	 * A media we were following has no registry record any more. Pull the row and compare its
	 * updated_at with the one held when following began: moved means processing finished,
	 * unchanged means the job died without a trace, reported as a failure (PR #1052 review).
	 */
	private async onLost(mediaId: number) {
		const before = this.markerWhenFollowed[mediaId] ?? null
		let after: string | null = null
		try {
			await DataHandlerMedia.pull([mediaId])
			after = this.completionMarker(mediaId)
		} catch (err) {
			console.error(`Could not re-check media ${mediaId} after its job record disappeared`, err)
		}
		if (after !== null && after !== before) {
			this.handleUploadSuccess(mediaId)
			return
		}
		console.error(
			`Processing for media ${mediaId} disappeared before reporting a result: nothing is running and it did not finish`,
		)
		this.handleUploadFail(mediaId)
	}

	private applyStatus(mediaId: number, entry: JobStatus) {
		switch (entry.state) {
			case "queued":
				this.rememberMarker(mediaId)
				this.progress[mediaId] = { progress: 0, status: "queued" }
				break
			case "running":
				this.rememberMarker(mediaId)
				this.progress[mediaId] = {
					progress: entry.progress ?? this.progress[mediaId]?.progress ?? 0,
					status: "processing",
				}
				break
			case "completed":
				this.handleUploadSuccess(mediaId)
				break
			case "failed":
				console.error(`Media ${mediaId} processing failed: ${entry.error ?? "no reason recorded"}`)
				this.handleUploadFail(mediaId)
				break
			case "cancelled":
				delete this.progress[mediaId]
				this.watched.delete(mediaId)
				break
		}
		if (!ACTIVE_JOB_STATES.includes(entry.state)) this.watched.delete(mediaId)
	}

	listenToProgress() {
		LuxedoRPC.bindEndpoint("mupload_set_progress", this.handleUploadProgress)
		LuxedoRPC.bindEndpoint("mupload_queued", this.handleUploadQueued)
		LuxedoRPC.bindEndpoint("mupload_process_complete", this.handleUploadSuccess)
		LuxedoRPC.bindEndpoint("mupload_process_fail", this.handleUploadFail)
	}
}

export const mediaProgressHandler = new MediaProgressHandler()
